import React from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Card from "../../components/ui/Card.jsx";
import {
  Lock,
  Eye,
  EyeOff,
  ArrowLeft,
  ShieldCheck,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { resetPasswordWithKey } from "../../api/borrowerPortal/resetPassword.js";

function getApiMessage(responseOrError) {
  if (!responseOrError || typeof responseOrError !== "object") return null;
  const data = responseOrError.data != null ? responseOrError.data : responseOrError;
  if (typeof data !== "object") return null;

  if (data._server_messages) {
    try {
      const raw = typeof data._server_messages === "string"
        ? JSON.parse(data._server_messages)
        : data._server_messages;
      const first = Array.isArray(raw) ? raw[0] : raw;
      const parsed = typeof first === "string" ? JSON.parse(first) : first;
      if (parsed?.message) return String(parsed.message).replace(/<[^>]*>/g, "").trim();
    } catch {
      /* fall through */
    }
  }

  const msg = data.message;
  if (Array.isArray(msg) && msg.length > 1 && typeof msg[1] === "string") return msg[1].trim();
  if (typeof msg === "string" && msg.trim()) return msg.trim();
  if (typeof msg?.message === "string" && msg.message.trim()) return msg.message.trim();

  const exc = data.exc || data.exception;
  if (typeof exc === "string" && exc.trim()) return exc.split("\n").pop().trim();

  return null;
}

/** Ignore unreplaced email-template placeholders like `{{ key }}`. */
function normalizeResetKey(raw) {
  const key = String(raw || "").trim();
  if (!key) return "";
  if (/\{\{\s*key\s*\}\}/i.test(key)) return "";
  if (/\[\[\s*key\s*\]\]/i.test(key)) return "";
  return key;
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const key = normalizeResetKey(searchParams.get("key"));
  const hasValidLink = Boolean(key);

  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!key) {
      return setError("This reset link is missing a key. Please use the link from your email.");
    }
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirmPassword) return setError("Passwords do not match.");

    setLoading(true);
    try {
      await resetPasswordWithKey({
        key,
        new_password: password,
      });
      setSuccess(true);
      setTimeout(() => {
        navigate("/borrower/signin", { replace: true });
      }, 2000);
    } catch (err) {
      const errData = err?.response?.data ?? err?.data ?? err;
      setError(
        getApiMessage(errData) ||
          err?.message ||
          "Failed to reset password. The link may have expired."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes slowFloat { 0% { transform: translateY(0) } 50% { transform: translateY(-6px) } 100% { transform: translateY(0) } }
        .logo-float { animation: slowFloat 6s ease-in-out infinite; }
        @keyframes glow { 0% { box-shadow: 0 0 0 rgba(56,189,248,0.0) } 50% { box-shadow: 0 8px 24px rgba(56,189,248,0.25) } 100% { box-shadow: 0 0 0 rgba(56,189,248,0.0) } }
        .card-glow { animation: glow 6s ease-in-out infinite; }
      `}</style>

      <div className="min-h-screen grid md:grid-cols-2 bg-slate-50">
        <div className="hidden md:flex relative items-center justify-start overflow-hidden pl-10">
          <div className="absolute inset-0 bg-gradient-to-b from-sky-500 to-sky-600" />
          <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute -bottom-20 -right-10 h-72 w-72 rounded-full bg-white/10 blur-2xl" />

          <div className="relative z-10 w-full max-w-4xl pr-6 text-white">
            <div className="flex items-center gap-0">
              <a href="/" className="shrink-0 -mr-3 md:-mr-5">
                <img
                  src="/dynesourcer-logo.svg"
                  alt="Dynesourcer polar bear"
                  className="h-36 md:h-48 w-auto logo-float select-none"
                  draggable="false"
                  loading="eager"
                  width="192"
                  height="192"
                />
              </a>
              <div className="min-w-0">
                <h1 className="md:text-5xl text-3xl font-extrabold tracking-tight">
                  Dynesourcer
                </h1>
                <p className="text-white/85 md:text-base text-sm mt-2">
                  Borrower Application Portal
                </p>
              </div>
            </div>

            <div className="mt-8 space-y-3">
              <h2 className="text-3xl font-bold leading-tight">Set a new password</h2>
              <p className="text-white/85">
                Choose a new password for your account. This link can only be used once.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center p-6">
          <Card className="w-full max-w-md rounded-2xl border-slate-200 bg-white p-6 shadow-sm card-glow">
            {!hasValidLink ? (
              <div className="py-2 text-center">
                <div className="rounded-full bg-rose-100 p-4 mb-4 inline-flex" aria-hidden>
                  <AlertCircle className="h-12 w-12 text-rose-600" />
                </div>
                <h2 className="text-xl font-semibold text-slate-900">Invalid reset link</h2>
                <p className="mt-3 text-sm text-slate-600">
                  This link must include a <code className="text-xs">key</code> in the URL. Please use the link
                  from your email, or request a new one.
                </p>
                <Link
                  to="/borrower/forgot-password"
                  className="mt-6 inline-flex items-center justify-center gap-2 rounded-xl bg-sky-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sky-600"
                >
                  Request a new link
                </Link>
              </div>
            ) : success ? (
              <div className="flex flex-col items-center text-center py-4">
                <div className="rounded-full bg-emerald-100 p-4 mb-4" aria-hidden>
                  <CheckCircle2 className="h-12 w-12 text-emerald-600" />
                </div>
                <h2 className="text-xl font-semibold text-slate-900">Password updated</h2>
                <p className="mt-3 text-sm text-slate-700">
                  Redirecting you to sign in...
                </p>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5 text-sky-500" />
                    <h2 className="text-xl font-semibold text-slate-900">Reset password</h2>
                  </div>
                  <p className="mt-1 text-sm text-slate-600">
                    Enter a new password for your account.
                  </p>
                </div>

                <form onSubmit={onSubmit} className="space-y-4">
                  <label className="block">
                    <span className="mb-1 block text-sm font-medium text-slate-700">New password</span>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 pr-10 outline-none focus:ring-2 focus:ring-sky-200"
                        placeholder="At least 8 characters"
                        autoComplete="new-password"
                        minLength={8}
                        disabled={loading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 rounded focus:outline-none focus:ring-2 focus:ring-sky-200"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </label>

                  <label className="block">
                    <span className="mb-1 block text-sm font-medium text-slate-700">Confirm password</span>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className={`w-full rounded-xl border bg-white px-3 py-2.5 pr-10 outline-none focus:ring-2 focus:ring-sky-200 ${
                          error && password !== confirmPassword ? "border-rose-300" : "border-slate-300"
                        }`}
                        placeholder="Re-enter your password"
                        autoComplete="new-password"
                        disabled={loading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 rounded focus:outline-none focus:ring-2 focus:ring-sky-200"
                        aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                        tabIndex={-1}
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </label>

                  {error && (
                    <p className="text-sm text-rose-600 flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />
                      {error}
                    </p>
                  )}

                  <div className="flex items-center gap-3">
                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex items-center justify-center gap-2 rounded-xl bg-sky-500 px-4 py-2.5 font-semibold text-white shadow hover:bg-sky-600 disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Lock className="h-4 w-4" />
                          Reset password
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => navigate("/borrower/signin")}
                      disabled={loading}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2 text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      Back
                    </button>
                  </div>
                </form>
              </>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}
